import { motion } from "framer-motion";
import { Shirt, Wand2, LayoutGrid, FileText } from "lucide-react";

const PRODUCTS = [
  {
    title: "Gen AI at Scale",
    description:
      "Campaign-grade visual content & PDP; generated, not shot. Brand-consistent imagery at a speed no studio can match.",
    icon: Wand2,
    image:
      "https://images.unsplash.com/photo-1763718528755-4bca23f82ac3?w=800&q=80&fit=crop",
    span: "md:col-span-6",
    badges: ["Reduce PDP turnaround timeline", "More GTM stories for social"],
    status: "Live Today",
    link: "https://wearemerai.com/gen-ai-at-scale",
    testId: "product-card-genai",
  },
  {
    title: "AI Techpacking",
    description:
      "Auto-generate production-ready techpacks from design inputs, built for how designers actually work.",
    icon: FileText,
    image:
      "https://images.pexels.com/photos/196646/pexels-photo-196646.jpeg?auto=compress&cs=tinysrgb&w=900",
    span: "md:col-span-6",
    badges: ["65% of design time reclaimed", "Collaborative design process"],
    status: "Pilot Deployments",
    testId: "product-card-techpacking",
  },
  {
    title: "AI Stylist + VTON",
    description:
      "Personalised outfit recommendations with virtual try-on. The fitting room, rebuilt for digital-first retail.",
    icon: Shirt,
    image:
      "https://images.pexels.com/photos/33714925/pexels-photo-33714925.jpeg?auto=compress&cs=tinysrgb&w=900",
    span: "md:col-span-6",
    badges: ["Increase cart size", "Reduce returns"],
    status: "Coming Next",
    testId: "product-card-vton",
  },
  {
    title: "AI VM + Planogramming",
    description:
      "Planogram intelligence and display optimisation. Real-time data-driven decisions on the floor, not instinct.",
    icon: LayoutGrid,
    image:
      "https://images.unsplash.com/photo-1532453288672-3a27e9be9efd?w=800&q=80&fit=crop",
    span: "md:col-span-6",
    badges: ["Quick VM decision making", "SOP formation for VM"],
    status: "Coming Next",
    testId: "product-card-visual-merch",
  },
];


const cardVariants = {
  hidden: { opacity: 0, y: 40 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, delay: i * 0.12, ease: [0.16, 1, 0.3, 1] }
  })
};

const STATUS_STYLES = {
  "Live Today": "bg-[#00FF66] text-[#0A0A0A]",
  "Pilot Deployments": "bg-[#0A0A0A] text-[#00FF66]",
  "Coming Next": "bg-[#0A0A0A]/10 text-[#0A0A0A]/60",
};

const ProductCard = ({ product, index }) => {
  const Icon = product.icon;
  return (
    <motion.div
      data-testid={product.testId}
      custom={index}
      variants={cardVariants}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-60px" }}
      className={`${product.span} bg-[#F4F4F0] overflow-hidden group cursor-default hover:-translate-y-1 hover:shadow-[6px_6px_0_0_#0A0A0A] transition-all duration-300 flex flex-col relative`}
    >
      {/* Status tag — top right */}
      {product.status && (
        <span className={`absolute top-4 right-4 font-mono text-[10px] uppercase tracking-widest font-bold px-2.5 py-1 ${STATUS_STYLES[product.status]}`}>
          {product.status}
        </span>
      )}

      {/* Content — fills full height */}
      <div className="p-6 md:p-8 flex flex-col flex-1">
        <Icon className="w-7 h-7 text-[#0A0A0A] mb-4 opacity-80 flex-shrink-0" strokeWidth={1.5} />
        <h3 className="font-cabinet text-xl font-bold uppercase text-[#0A0A0A] mb-3 tracking-tight flex-shrink-0">
          {product.title}
        </h3>
        <p className="font-manrope text-sm text-[#4A4A4A] leading-relaxed flex-1">
          {product.description}
        </p>
        {product.badges?.length > 0 && (
          <div className="mt-5 flex flex-col gap-2 flex-shrink-0">
            {product.badges.map((badge) => (
              <span
                key={badge}
                className="inline-flex items-center gap-1.5 bg-[#0A0A0A] text-[#00FF66] font-mono text-xs uppercase tracking-wider px-3 py-1.5"
              >
                <span>↑</span> {badge}
              </span>
            ))}
          </div>
        )}
        {product.link && (
          <a
            href={product.link}
            data-testid={`${product.testId}-link`}
            className="mt-6 inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest font-bold text-[#0A0A0A] border-b border-[#0A0A0A]/40 pb-0.5 hover:border-[#0A0A0A] transition-colors self-start flex-shrink-0"
          >
            VIEW PRODUCT →
          </a>
        )}
      </div>
    </motion.div>
  );
};

const Products = () =>
<section
  id="products"
  data-testid="products-section"
  className="bg-[#E8380D] py-24 md:py-32">
  
    <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-24">
      {/* Section Header */}
      <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      viewport={{ once: true }}
      className="mb-12 md:mb-16">
      
        <p className="font-mono text-xs uppercase tracking-[0.2em] font-bold text-black/50 mb-3">
          What We Build
        </p>
        <h2 className="font-cabinet text-4xl sm:text-5xl font-bold tracking-tight text-[#0A0A0A] uppercase leading-tight">
          FOUR PRODUCTS.
          <br />
          ONE INTELLIGENCE LAYER.
        </h2>
      </motion.div>

      {/* Bento Grid */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-5">
        {PRODUCTS.map((product, i) =>
      <ProductCard key={product.testId} product={product} index={i} />
      )}
      </div>
    </div>
  </section>;


export default Products;